import { Helmet } from 'react-helmet-async';

// Site configuration
export const siteConfig = {
  siteName: 'NYRAV Tours and Travels',
  siteUrl: process.env.REACT_APP_SITE_URL || 'https://nyravtours.com',
  defaultTitle: 'NYRAV Tours and Travels - Your Gateway to Unforgettable Adventures',
  defaultDescription: 'Discover unforgettable travel experiences with NYRAV Tours and Travels. Book domestic and international tour packages, honeymoon trips, corporate travel, and custom adventures across India and worldwide.',
  defaultKeywords: 'tours, travels, india, packages, honeymoon, adventure, vacation, holiday, domestic tours, international tours, travel agency, tour operator',
  defaultImage: '/og-image.jpg',
  twitterHandle: '@nyravtours',
  facebookAppId: '',
  locale: 'en_IN',
  organization: {
    name: 'NYRAV Tours and Travels',
    legalName: 'NYRAV Tours and Travels Pvt. Ltd.',
    url: 'https://nyravtours.com',
    logo: 'https://nyravtours.com/logo.png',
    foundingDate: '2010',
    founders: ['NYRAV Team'],
    address: {
      streetAddress: '44, PB Road, Suhagpur, Rehabari',
      addressLocality: 'Guwahati',
      addressRegion: 'Assam',
      postalCode: '781008',
      addressCountry: 'IN'
    },
    contactPoint: {
      telephone: '+91-9181268900',
      contactType: 'customer service',
      availableLanguage: ['English', 'Hindi']
    },
    sameAs: [
      'https://www.facebook.com/nyravtours',
      'https://www.instagram.com/nyravtours',
      'https://twitter.com/nyravtours',
      'https://www.youtube.com/nyravtours',
      'https://www.linkedin.com/company/nyravtours'
    ]
  }
};

/**
 * Main SEO Component with comprehensive meta tags and JSON-LD
 */
export function SEO({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  article = null,
  product = null,
  breadcrumbs = null,
  noIndex = false,
  children
}) {
  const seo = {
    title: title ? `${title} | ${siteConfig.siteName}` : siteConfig.defaultTitle,
    description: description || siteConfig.defaultDescription,
    keywords: keywords || siteConfig.defaultKeywords,
    image: image?.startsWith('http') ? image : `${siteConfig.siteUrl}${image || siteConfig.defaultImage}`,
    url: url || siteConfig.siteUrl,
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seo.title}</title>
      <meta name="title" content={seo.title} />
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      <meta name="author" content={siteConfig.siteName} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={seo.url} />
      
      {/* Language and Locale */}
      <html lang="en" />
      <meta property="og:locale" content={siteConfig.locale} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={seo.title} />
      <meta property="og:site_name" content={siteConfig.siteName} />
      {siteConfig.facebookAppId && (
        <meta property="fb:app_id" content={siteConfig.facebookAppId} />
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={siteConfig.twitterHandle} />
      <meta name="twitter:creator" content={siteConfig.twitterHandle} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:image:alt" content={seo.title} />
      
      {/* Article specific meta (for blog posts) */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedAt} />
          <meta property="article:modified_time" content={article.modifiedAt || article.publishedAt} />
          <meta property="article:author" content={article.author || siteConfig.siteName} />
          <meta property="article:section" content={article.category || 'Travel'} />
          {article.tags?.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Product specific meta (for tour packages) */}
      {product && (
        <>
          <meta property="product:price:amount" content={product.price} />
          <meta property="product:price:currency" content="INR" />
          <meta property="product:availability" content={product.available ? 'in stock' : 'out of stock'} />
          <meta property="product:brand" content={siteConfig.siteName} />
        </>
      )}
      
      {/* Mobile App Meta */}
      <meta name="theme-color" content="#004E64" />
      <meta name="msapplication-TileColor" content="#004E64" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content={siteConfig.siteName} />
      
      {/* Verification Tags (add your verification codes) */}
      {/* <meta name="google-site-verification" content="your-google-verification-code" /> */}
      {/* <meta name="msvalidate.01" content="your-bing-verification-code" /> */}
      
      {/* Additional children (for page-specific scripts/meta) */}
      {children}
    </Helmet>
  );
}

/**
 * Organization Schema (JSON-LD)
 */
export function OrganizationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    '@id': `${siteConfig.siteUrl}/#organization`,
    name: siteConfig.organization.name,
    legalName: siteConfig.organization.legalName,
    url: siteConfig.organization.url,
    logo: {
      '@type': 'ImageObject',
      url: siteConfig.organization.logo,
      width: 512,
      height: 512
    },
    image: siteConfig.organization.logo,
    description: siteConfig.defaultDescription,
    foundingDate: siteConfig.organization.foundingDate,
    address: {
      '@type': 'PostalAddress',
      ...siteConfig.organization.address
    },
    contactPoint: {
      '@type': 'ContactPoint',
      ...siteConfig.organization.contactPoint
    },
    sameAs: siteConfig.organization.sameAs,
    priceRange: '₹₹₹'
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * Website Schema (JSON-LD)
 */
export function WebsiteSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteConfig.siteUrl}/#website`,
    url: siteConfig.siteUrl,
    name: siteConfig.siteName,
    description: siteConfig.defaultDescription,
    publisher: {
      '@id': `${siteConfig.siteUrl}/#organization`
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteConfig.siteUrl}/tours?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    },
    inLanguage: 'en-IN'
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * Breadcrumb Schema (JSON-LD)
 */
export function BreadcrumbSchema({ items }) {
  if (!items || items.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? `${siteConfig.siteUrl}${item.url}` : undefined
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * Tour Package Schema (Product Schema - JSON-LD)
 */
export function TourPackageSchema({ tour }) {
  if (!tour) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${siteConfig.siteUrl}/tours/${tour.slug}`,
    name: tour.name || tour.title,
    description: tour.description,
    image: tour.images || [],
    brand: {
      '@type': 'Brand',
      name: siteConfig.siteName
    },
    offers: {
      '@type': 'Offer',
      url: `${siteConfig.siteUrl}/tours/${tour.slug}`,
      priceCurrency: 'INR',
      price: tour.discounted_price || tour.price,
      ...(tour.discounted_price && tour.priceValidUntil && {
        priceValidUntil: tour.priceValidUntil
      }),
      availability: tour.status === 'active' ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: siteConfig.siteName
      }
    },
    ...(tour.rating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: tour.rating,
        reviewCount: tour.reviewCount || 1,
        bestRating: 5,
        worstRating: 1
      }
    }),
    additionalProperty: [
      {
        '@type': 'PropertyValue',
        name: 'Duration',
        value: `${tour.duration_days} Days / ${tour.duration_nights} Nights`
      },
      {
        '@type': 'PropertyValue',
        name: 'Destination',
        value: tour.destination
      },
      {
        '@type': 'PropertyValue',
        name: 'Category',
        value: tour.category
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * Tour/Travel Service Schema (JSON-LD)
 */
export function TourServiceSchema({ tour }) {
  if (!tour) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'TouristTrip',
    name: tour.name || tour.title,
    description: tour.description,
    image: tour.images?.[0],
    touristType: tour.category,
    itinerary: {
      '@type': 'ItemList',
      numberOfItems: tour.itinerary?.length || tour.duration_days,
      itemListElement: tour.itinerary?.map((day, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: day.title,
        description: day.description
      })) || []
    },
    offers: {
      '@type': 'Offer',
      price: tour.discounted_price || tour.price,
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock'
    },
    provider: {
      '@type': 'TravelAgency',
      name: siteConfig.siteName,
      url: siteConfig.siteUrl
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * Review/Testimonial Schema (JSON-LD)
 */
export function ReviewSchema({ review, itemReviewed }) {
  if (!review) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: review.customer_name || review.name
    },
    datePublished: review.created_at,
    reviewBody: review.review || review.content,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.rating,
      bestRating: 5,
      worstRating: 1
    },
    itemReviewed: itemReviewed || {
      '@type': 'TravelAgency',
      name: siteConfig.siteName
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * Multiple Reviews/Testimonials Schema (JSON-LD)
 */
export function TestimonialsSchema({ testimonials }) {
  if (!testimonials || testimonials.length === 0) return null;

  const avgRating = testimonials.reduce((sum, t) => sum + (t.rating || 5), 0) / testimonials.length;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    name: siteConfig.siteName,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: avgRating.toFixed(1),
      reviewCount: testimonials.length,
      bestRating: 5,
      worstRating: 1
    },
    review: testimonials.map(t => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: t.customer_name || t.name
      },
      datePublished: t.created_at,
      reviewBody: t.review || t.content,
      reviewRating: {
        '@type': 'Rating',
        ratingValue: t.rating || 5,
        bestRating: 5
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * Article/Blog Post Schema (JSON-LD)
 */
export function ArticleSchema({ article }) {
  if (!article) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${siteConfig.siteUrl}/blog/${article.slug}`,
    headline: article.title,
    description: article.excerpt || article.content?.substring(0, 160),
    image: article.featured_image || article.featuredImage,
    datePublished: article.published_at || article.created_at,
    dateModified: article.updated_at || article.created_at,
    author: {
      '@type': 'Person',
      name: article.author || siteConfig.siteName
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.siteName,
      logo: {
        '@type': 'ImageObject',
        url: siteConfig.organization.logo
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteConfig.siteUrl}/blog/${article.slug}`
    },
    articleSection: article.category,
    keywords: article.tags?.join(', ')
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * FAQ Schema (JSON-LD)
 */
export function FAQSchema({ faqs }) {
  if (!faqs || faqs.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * Local Business Schema (JSON-LD)
 */
export function LocalBusinessSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    '@id': `${siteConfig.siteUrl}/#localbusiness`,
    name: siteConfig.organization.name,
    image: siteConfig.organization.logo,
    url: siteConfig.siteUrl,
    telephone: siteConfig.organization.contactPoint.telephone,
    email: 'nyravtravels@gmail.com',
    address: {
      '@type': 'PostalAddress',
      ...siteConfig.organization.address
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 19.0760,
      longitude: 72.8777
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:00',
        closes: '18:00'
      }
    ],
    priceRange: '₹₹₹',
    sameAs: siteConfig.organization.sameAs
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// Export everything
export { siteConfig as config };
